import { LoanApplicationReasonEnum } from "./loanApplication";

export enum FieldRequestFieldChoices {
  ACCOUNTING_DATA = "accounting_data",
  SELF_ESTIMATED_REVENUE = "self_estimated_revenue",
  SELF_ESTIMATED_HISTORIC_REVENUE = "self_estimated_historic_revenue",
  GUARANTORS = "guarantors",
  REFERRER = "referrer",
  BANKING_DATA = "banking_data",
  PROPERTY = "property",
  PROPERTY_ADDITIONAL_DATA = "property_additional_data",
  PROPERTY_OWNER = "property_owner",
  ADDITIONAL_SECURITIES = "additional_securities",
  UNPAID_INVOICES = "unpaid_invoices",
  UNPAID_INVOICES_ADDITIONAL_DATA = "unpaid_invoices_additional_data",
  COMPANY_MORTGAGE = "company_mortgage",
  BUDGET = "budget",
  BUSINESS_PLAN = "business_plan",
  REASON = "reason",
  CORONA = "corona",
  REQUESTED_AMOUNT = "requested_amount",
  REPAYMENT_TIME = "repayment_time",
  SUGGESTED_AMOUNT = "suggested_amount",
  PAWNED_INVOICES = "pawned_invoices",
  PARENT_COMPANY_GUARANTEE = "parent_company_guarantee",
  SHARE_MORTGAGE = "share_mortgage",
  REFINANCING = "refinancing",
  PROPERTY_CASH_FLOW = "property_cash_flow",
  CONSTRUCTION_COMPLETED_REPAYMENT = "construction_completed_repayment",
  CONSTRUCTION_ESTIMATE = "construction_estimate",
  BUILDING_PERMIT = "building_permit",
  GROUPED_REAL_ESTATE_COLLATERAL = "grouped_real_estate_collateral",
  GROUPED_REAL_ESTATE_REPAYMENT = "grouped_real_estate_repayment",
  REFINANCING_AMOUNTS = "refinancing_amounts",
  LEASING = "leasing",
  LEASING_ADDITIONAL_DATA = "leasing_additional_data",
  REASON_EXPLANATION_FIELD = "reason_explanation",
}

export enum FieldRequestTypeEnum {
  COLLATERAL = "COLLATERAL",
  ADDITIONAL_INFORMATION = "ADDITIONAL_INFORMATION",
}

export enum RealEstateRepaymentOfLoanMethodEnum {
  REFINANCING = "REFINANCING",
  PROPERTY_REVENUE = "PROPERTY_REVENUE",
  COMPANY_REVENUE = "COMPANY_REVENUE",
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface IFieldRequest<T = never> {
  id: string;
  createdAt: string;
  updatedAt: string;
  field: FieldRequestFieldChoices;
  lenderComment: string;
  type: FieldRequestTypeEnum;
  required: boolean;
  improveApplicationDescription: string;
  isSubmitted: boolean;
  displayName: string;
  subtitle: string;
  displayText: string;
  submittedData: T | null;
  isCollectedByLender: boolean;
}

export interface IFieldRequestFile {
  url: string;
  original_file_name: string;
  file_id: string;
  created_at: string;
}

export interface IGuarantorsFRSubmittedData {
  guarantors: {
    id: string;
    ssn: string;
    email?: string | null;
    mobile_phone_number?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    requested_to_be_replaced?: boolean;
    banking_data?: {
      estimated_monthly_income?: number | null;
    } | null;
  }[];
}

export interface IRefinancingAmount {
  lender_name: string;
  outstanding_amount: number | null;
  monthly_cost: number | null;
}

export interface IRefinancingAmountsFRSubmittedData {
  refinancing_amounts: IRefinancingAmount[];
  files: IFieldRequestFile[];
}

export interface IUnpaidInvoicesFRSubmittedData {
  estimated_invoice_amount: number | null;
  number_of_invoices: number | null;
  average_payment_time_days: number | null;
  invoices_description: string | null;
}

export interface IUnpaidInvoicesAdditionalDataFRSubmittedData {
  files: IFieldRequestFile[];
}

export interface ILeasingFRSubmittedData {
  leasing_amount: number | null;
  leasing_product_type: string | null;
}

export interface ILeasingAdditionalDataFRSubmittedData {
  leasing_product_url: string | null;
  leasing_product_description: string | null;
  files: IFieldRequestFile[];
}

export interface IAdditionalSecuritiesFRSubmittedData {
  additional_securities_text: string | null;
  files: IFieldRequestFile[];
}

export interface IBudgetFRSubmittedData {
  files: IFieldRequestFile[];
}

export interface IBusinessPlanFRSubmittedData {
  files: IFieldRequestFile[];
}

export interface IPropertyDataFRSubmittedData {
  property_unit_designation: string | null;
  // property_current_mortgage: string | null;
}

export interface IPropertyAdditionalDataFRSubmittedData {
  property_valuation: number | null;
  property_url: string | null;
  files: IFieldRequestFile[];
}

export interface IParentCompanyGuaranteeFRSubmittedData {
  files: IFieldRequestFile[];
}

export interface IShareMortgageFRSubmittedData {
  share_mortgage_text: string | null;
}

export interface IBuildingPermitFRSubmittedData {
  files: IFieldRequestFile[];
}

export interface IConstructionEstimateFRSubmittedData {
  files: IFieldRequestFile[];
}

// constructionCompletedRepayment: FieldRequestInterface;
export interface GroupedFieldRequestSubmittedDataChoice {
  name: string;
  value: RealEstateRepaymentOfLoanMethodEnum;
  selected: boolean;
}

export interface IGroupedFieldRequestFRSubmittedData {
  choices: GroupedFieldRequestSubmittedDataChoice[];
}

export interface IRefinancingFRSubmittedData {
  refinancing_text: string | null;
}

export interface IPropertyCashFlowFRSubmittedData {
  files: IFieldRequestFile[];
}

export interface IAccountingDataFRSubmittedData {
  files: IFieldRequestFile[];
}

export interface IConstructionCompletedRepaymentFRSubmittedData {
  construction_completed_repayment_text: string | null;
}

export type SelfEstimatedRevenueFRSubmittedDataInterface = string | null; // decimal

export type SelfEstimatedHistoricRevenueFRSubmittedDataInterface =
  | string // decimal
  | null;

export interface IBankingDataFRSubmittedData {
  structured_data: {
    bank_account_id: string;
    bank_account_name?: string | null;
    available_amount?: number | null;
    explicitly_selected_by_user: boolean;
    bank_name?: string | null;
  }[];
  has_integration_data: boolean;
  files: IFieldRequestFile[];
}

export interface IReasonExplanationFRSubmittedData {
  explanation: string;
}

export type IRepaymentTimeFRSubmittedData = number | null;

export type ISelfEstimatedRevenueFRSubmittedData = number | null;

export interface IReasonFRSubmittedData {
  reason: LoanApplicationReasonEnum;
}

export interface IRequestedAmountFRSubmittedData {
  requested_loan_amount: number | null;
}

export interface IPropertyOwnerFRSubmittedData {
  is_property_owner: boolean;
}
